/**
 * Zustand store for Video Workspace state management
 * Handles panel states, filmstrip modes, slide navigation, and player state
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { UnifiedSlide } from '@/types/gsapSlide';
import type { VersionInfo } from '@/components/slides/VersionHistoryPanel';
import type { VideoOutline, SlideOutline, TemplateLayout } from '@/types/outline';
import type { SlideScript } from '@/components/video-workspace/narration/ScriptsList';
import type { SlideAudioStatusSummary, TTSVoice } from '@/services/audioService';
import type { OutlineChangeQueueItem } from '@/types/outlineChangeQueue';

// Re-export VersionInfo for consumers of this store
export type { VersionInfo };
// Re-export outline types for convenience
export type { VideoOutline, SlideOutline, TemplateLayout };

// Slide conversation for context switching
export type SlideGenerationStatus = 'pending' | 'generating' | 'completed' | 'failed';

export interface SlideConversation {
  id: string;                           // Conversation ID
  outlineSlideId: string;               // e.g., "slide-001"
  title: string;
  status: SlideGenerationStatus;
  generatingThinking?: string;          // Live thinking text during generation
  errorMessage?: string;                // If failed
}

// Broadcast message for sending to all slides at once
export interface BroadcastMessage {
  id: string;
  content: string;
  timestamp: Date;
  slideCount: number;  // How many slides it was sent to
}

// Re-export Slide type for convenience
export interface Slide {
  id: string;
  title: string;
  thumbnail?: string;
  duration: number;
  hasAudio?: boolean;
  path?: string;
  currentVersion?: number;  // Version number from API for cache invalidation
  // For video workflow - each slide is in a child conversation
  childConversationId?: string;
  status?: SlideGenerationStatus;
}

// Cached slide data for smart caching
export interface CachedSlide {
  data: UnifiedSlide;
  version: number;
  cachedAt: number;
}

export interface PlayerState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  playbackRate: number;
  isLoading: boolean;
  slideTitle: string;
}

// Background music state for video-level music (and audio mix settings)
export interface BackgroundMusicState {
  track: {
    id: string;
    title: string;
    url: string;
    duration: number;
    source: 'freesound' | 'uploaded' | 'url';
  } | null;
  volume: number;  // 0-1 (background music volume)
  loop: boolean;
  isApplied: boolean;  // Whether it's been applied to the manifest
  narratorVolume: number;  // 0-1 (narrator/TTS volume), default 1.0
}

export interface EditToolbarState {
  selectedCount: number;
  canUndo: boolean;
  canRedo: boolean;
}

// Pending slide update - queued when a slide update arrives while editing
export interface PendingSlideUpdate {
  conversationId: string;
  slideId: string;
  timestamp: number;
}

// Narrative context structure from video-scripts.json
export interface NarrativeContext {
  openingHook: string;
  keyTheme: string;
  tone: string;
  resolution: string;
  baseVoiceStyle?: {
    pace: string;
    personality: string;
    tone: string;
  };
  // Single TTS prompt (preferred)
  audioPrompt?: string;
  // Legacy fields for backward compatibility
  videoPromptFlash?: string;
  videoPromptPro?: string;
}

// Narration cache - preloaded data for instant tab switching
export interface NarrationCache {
  scripts: SlideScript[];
  audioSettings: { voiceId: string; ttsModel: string } | null;
  audioStatus: SlideAudioStatusSummary[];
  hasNarrativeContext: boolean;
  narrativeContext: NarrativeContext | null;
  lastFetchedAt: number;
}

// Narration job state - tracks active script generation jobs (persists across navigation)
export interface NarrationJobState {
  isGenerating: boolean;
  activeJobId: string | null;
  activeMessageId: string | null;
  generatingThinking?: string;
  lastUpdatedAt: number;
}

// Apply job state - tracks active audio apply jobs per slide (persists across navigation/refresh)
export interface ApplyJobProgress {
  thinking?: string;
  currentOperation?: string;
  percentage?: number;
}

export interface ApplyJobState {
  // Map of slideId -> job state
  slideJobs: Record<string, {
    jobId: string;
    status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
    progress: ApplyJobProgress;
    startedAt: number;
  }>;
  // Whether Apply All is in progress
  isApplyingAll: boolean;
  lastUpdatedAt: number;
}

// Voices cache - global since voices don't change per conversation
export interface VoicesCache {
  voices: TTSVoice[];
  defaultVoice: string;
  lastFetchedAt: number;
}

type FilmstripMode = 'horizontal' | 'vertical';
type InspectorTab = 'script' | 'audio' | 'versions' | 'settings';
type LeftPanelView = 'chat' | 'deck';  // What to show in left panel area when in vertical mode

interface VideoWorkspaceState {
  // Panel States (global - user preferences)
  leftPanelCollapsed: boolean;
  rightPanelCollapsed: boolean;
  leftPanelWidth: number;
  leftPanelView: LeftPanelView;  // Toggle between chat and deck in vertical mode

  // Filmstrip (global - user preferences)
  filmstripMode: FilmstripMode;
  filmstripCollapsed: boolean;

  // === KEYED BY CONVERSATION ID ===
  // Slides - keyed by conversationId for conversation isolation
  slidesByConversation: Record<string, Slide[]>;
  currentSlideIdByConversation: Record<string, string | null>;
  currentSlideDataByConversation: Record<string, UnifiedSlide | null>;

  // Outline state - keyed by conversationId
  outlineByConversation: Record<string, VideoOutline | null>;
  selectedOutlineSlideIdByConversation: Record<string, string | null>;

  // Chat context switching - keyed by parent conversationId
  // Values: 'outline' | 'narration' | 'broadcast' | slide conversation ID
  activeChatContextByConversation: Record<string, 'outline' | 'narration' | 'broadcast' | string>;
  slideConversationsByParent: Record<string, SlideConversation[]>;

  // Broadcast log - keyed by parent conversationId
  broadcastLogByConversation: Record<string, BroadcastMessage[]>;

  // Versions - keyed by "conversationId:slideId"
  versionsBySlide: Record<string, VersionInfo[]>;
  currentVersionBySlide: Record<string, number | null>;

  // Server cache pin for /serve fetches ("v=8&aa=1753..."), harvested from the
  // manifest's pinned jsonPath - keyed by "conversationId:slideId". The pin
  // rotates on every edit AND every audio apply, so pinned panel fetches can
  // use the immutable browser cache without ever reading a stale in-place write.
  servePinBySlide: Record<string, string>;

  // Background music - keyed by conversationId (video-level music)
  backgroundMusicByConversation: Record<string, BackgroundMusicState>;

  // Narration cache - keyed by conversationId
  narrationCacheByConversation: Record<string, NarrationCache>;
  narrationCacheLoading: boolean;
  voicesCache: VoicesCache | null;

  // Narration job state - keyed by conversationId (persists across navigation)
  narrationJobStateByConversation: Record<string, NarrationJobState>;

  // Apply job state - keyed by conversationId (persists across navigation/refresh)
  applyJobStateByConversation: Record<string, ApplyJobState>;

  // Outline change queue - keyed by conversationId
  outlineChangeQueueByConversation: Record<string, OutlineChangeQueueItem[]>;

  // === GLOBAL STATE ===
  // Loading state (global - one active operation at a time)
  isSlideLoading: boolean;

  // Player State (synced from iframe - global, tied to current view)
  playerState: PlayerState;

  // Edit Mode (global - tied to current view)
  isEditMode: boolean;
  editToolbarState: EditToolbarState;

  // Edit isolation - track which specific slide is being edited
  editingSlideId: string | null;           // The slideId currently in edit mode
  editingConversationId: string | null;    // The conversation containing the editing slide
  pendingSlideUpdate: PendingSlideUpdate | null;  // Queued update for the editing slide

  // Inspector (global - user preference)
  activeInspectorTab: InspectorTab;

  // Preview refresh key - increment to force iframe reload
  previewRefreshKey: number;

  // Pending seek time - used to restore playback position after version switch
  // When set, the slide preview should seek to this time instantly after loading
  pendingSeekTime: number | null;

  // Preview mode - when true, seek to end of slide after loading for filmstrip preview
  // This shows the completed slide state instead of the initial blank state
  previewSeekToEnd: boolean;

  // Active conversation context (which conversation is currently displayed)
  conversationId: string | null;

  // Slide cache - already keyed by "conversationId:slideId"
  slideCache: Map<string, CachedSlide>;

  // Outline loading state (global - one load at a time)
  outlineLoading: boolean;

  // Template layouts (shared across conversations)
  templateLayouts: TemplateLayout[];

  // Outline generation state (global - one generation at a time)
  isOutlineGenerating: boolean;
  outlineGeneratingThinking: string | undefined;
  outlineNeedsInput: boolean;

  // Outline local edit tracking - when set (non-null), server-fetched outlines
  // should merge instead of wholesale replace to avoid overwriting user edits
  outlineLocalEditTimestamp: number | null;

  // === DEPRECATED - Keep for backwards compatibility during migration ===
  // These will be removed once all consumers are migrated
  slides: Slide[];
  currentSlideId: string | null;
  /** Timestamp of the last user-initiated filmstrip selection — auto-nav respects it. */
  lastManualSlideSelectAt: number;
  /** The scene a pick sent the composition canvas to, until the canvas reports it. */
  canvasTarget: { slideId: string; at: number } | null;
  /** Per-slide cache-bust token (bumped on sync) so the filmstrip thumbnail reloads, keyed by slideId. */
  slideThumbBust: Record<string, number>;
  currentSlideData: UnifiedSlide | null;
  outline: VideoOutline | null;
  selectedOutlineSlideId: string | null;
  versions: VersionInfo[];
  currentVersion: number | null;
  activeChatContext: 'outline' | 'narration' | 'broadcast' | string;
  slideConversations: SlideConversation[];
}

interface VideoWorkspaceActions {
  // Panel Actions
  setLeftPanelCollapsed: (collapsed: boolean) => void;
  setRightPanelCollapsed: (collapsed: boolean) => void;
  toggleLeftPanel: () => void;
  toggleRightPanel: () => void;
  setLeftPanelWidth: (width: number) => void;
  setLeftPanelView: (view: LeftPanelView) => void;
  toggleLeftPanelView: () => void;

  // Filmstrip Actions
  setFilmstripMode: (mode: FilmstripMode) => void;
  toggleFilmstripMode: () => void;
  setFilmstripCollapsed: (collapsed: boolean) => void;
  toggleFilmstrip: () => void;

  // === SCOPED SLIDE ACTIONS (requires conversationId) ===
  getSlidesForConversation: (conversationId: string) => Slide[];
  getCurrentSlideIdForConversation: (conversationId: string) => string | null;
  getCurrentSlideDataForConversation: (conversationId: string) => UnifiedSlide | null;
  setSlidesForConversation: (conversationId: string, slides: Slide[]) => void;
  setCurrentSlideIdForConversation: (conversationId: string, id: string | null) => void;
  setCurrentSlideDataForConversation: (conversationId: string, data: UnifiedSlide | null) => void;
  /** Applies a scene's loaded slide (or null when its load failed) only if that workspace and scene are still on
   *  screen; a load that lands after the user moved on is dropped. Returns whether it was applied. */
  applyLoadedSlide: (conversationId: string, slideId: string, data: UnifiedSlide | null) => boolean;
  applyLoadedVersions: (conversationId: string, slideId: string, versions: VersionInfo[], currentVersion: number | null) => boolean;
  navigateToSlideForConversation: (conversationId: string, direction: 'next' | 'prev') => void;

  // === SCOPED OUTLINE ACTIONS (requires conversationId) ===
  getOutlineForConversation: (conversationId: string) => VideoOutline | null;
  getSelectedOutlineSlideIdForConversation: (conversationId: string) => string | null;
  setOutlineForConversation: (conversationId: string, outline: VideoOutline | null) => void;
  setSelectedOutlineSlideForConversation: (conversationId: string, id: string | null) => void;
  updateOutlineTitleForConversation: (conversationId: string, title: string) => void;
  updateOutlineSlideForConversation: (conversationId: string, slideId: string, updates: Partial<SlideOutline>) => void;
  reorderOutlineSlidesForConversation: (conversationId: string, fromIndex: number, toIndex: number) => void;
  addOutlineSlideForConversation: (conversationId: string, afterSlideId?: string) => void;
  removeOutlineSlideForConversation: (conversationId: string, slideId: string) => void;

  // === SCOPED VERSION ACTIONS (requires conversationId + slideId) ===
  getVersionsForSlide: (conversationId: string, slideId: string) => VersionInfo[];
  getCurrentVersionForSlide: (conversationId: string, slideId: string) => number | null;
  setVersionsForSlide: (conversationId: string, slideId: string, versions: VersionInfo[]) => void;
  setCurrentVersionForSlide: (conversationId: string, slideId: string, version: number | null) => void;
  getServePinForSlide: (conversationId: string, slideId: string) => string | null;
  setServePinForSlide: (conversationId: string, slideId: string, pin: string) => void;

  // === SCOPED CHAT CONTEXT ACTIONS (requires conversationId) ===
  getActiveChatContextForConversation: (conversationId: string) => 'outline' | 'narration' | 'broadcast' | string;
  getSlideConversationsForParent: (conversationId: string) => SlideConversation[];
  setActiveChatContextForConversation: (conversationId: string, context: 'outline' | 'narration' | 'broadcast' | string) => void;
  setSlideConversationsForParent: (conversationId: string, conversations: SlideConversation[]) => void;
  updateSlideConversationForParent: (conversationId: string, outlineSlideId: string, updates: Partial<SlideConversation>) => void;
  addSlideConversationForParent: (conversationId: string, conversation: SlideConversation) => void;

  // === SCOPED BROADCAST LOG ACTIONS (requires conversationId) ===
  getBroadcastLogForConversation: (conversationId: string) => BroadcastMessage[];
  addBroadcastMessage: (conversationId: string, message: BroadcastMessage) => void;
  clearBroadcastLog: (conversationId: string) => void;

  // === SCOPED BACKGROUND MUSIC ACTIONS (requires conversationId) ===
  getBackgroundMusicForConversation: (conversationId: string) => BackgroundMusicState | null;
  setBackgroundMusicForConversation: (conversationId: string, track: BackgroundMusicState['track']) => void;
  updateBackgroundMusicVolumeForConversation: (conversationId: string, volume: number) => void;
  updateBackgroundMusicLoopForConversation: (conversationId: string, loop: boolean) => void;
  updateNarratorVolumeForConversation: (conversationId: string, volume: number) => void;
  setBackgroundMusicAppliedForConversation: (conversationId: string, isApplied: boolean) => void;
  removeBackgroundMusicForConversation: (conversationId: string) => void;
  initializeAudioStateFromManifest: (conversationId: string, config: { backgroundMusic?: { url: string; volume: number; loop: boolean }; audioMix?: { narratorVolume: number } }) => void;

  // === SCOPED NARRATION CACHE ACTIONS (requires conversationId) ===
  getNarrationCache: (conversationId: string) => NarrationCache | null;
  setNarrationCache: (conversationId: string, cache: NarrationCache) => void;
  updateScriptsInCache: (conversationId: string, scripts: SlideScript[]) => void;
  updateAudioStatusInCache: (conversationId: string, status: SlideAudioStatusSummary[]) => void;
  updateNarrativeContextInCache: (conversationId: string, context: NarrativeContext | null) => void;
  invalidateNarrationCache: (conversationId: string) => void;
  setVoicesCache: (voices: TTSVoice[], defaultVoice: string) => void;
  preloadNarrationData: (conversationId: string) => Promise<void>;

  // === PROJECT RESOLUTION (derived from narration cache) ===
  getProjectResolution: (conversationId: string) => { width: number; height: number };

  // === SCOPED NARRATION JOB STATE ACTIONS (requires conversationId) ===
  getNarrationJobState: (conversationId: string) => NarrationJobState | null;
  setNarrationJobState: (conversationId: string, state: Partial<NarrationJobState>) => void;
  clearNarrationJobState: (conversationId: string) => void;

  // === SCOPED APPLY JOB STATE ACTIONS (requires conversationId) ===
  getApplyJobState: (conversationId: string) => ApplyJobState | null;
  setApplyJobState: (conversationId: string, state: Partial<ApplyJobState>) => void;
  updateSlideApplyJob: (conversationId: string, slideId: string, job: ApplyJobState['slideJobs'][string] | null) => void;
  clearApplyJobState: (conversationId: string) => void;

  // === SCOPED OUTLINE CHANGE QUEUE ACTIONS (requires conversationId) ===
  addOutlineChange: (conversationId: string, item: OutlineChangeQueueItem) => void;
  removeOutlineChange: (conversationId: string, itemId: string) => void;
  clearOutlineChangeQueue: (conversationId: string) => void;
  getOutlineChangeQueue: (conversationId: string) => OutlineChangeQueueItem[];

  // === DEPRECATED - Legacy actions kept for backwards compatibility ===
  // These sync to both keyed and legacy state during migration
  setSlides: (slides: Slide[]) => void;
  bumpSlideThumbBust: (slideIds: string[]) => void;
  setCurrentSlideId: (id: string | null) => void;
  markManualSlideSelect: () => void;
  setCanvasTarget: (slideId: string) => void;
  /** The scene the composition canvas reports (playback, a seek): the studio moves there unless a pick is on its way. */
  followCanvasScene: (slideId: string) => void;
  setCurrentSlideData: (data: UnifiedSlide | null) => void;
  setSlideLoading: (loading: boolean) => void;
  navigateToSlide: (direction: 'next' | 'prev') => void;
  setVersions: (versions: VersionInfo[]) => void;
  setCurrentVersion: (version: number | null) => void;
  setOutline: (outline: VideoOutline | null) => void;
  setSelectedOutlineSlide: (id: string | null) => void;
  updateOutlineTitle: (title: string) => void;
  updateOutlineSlide: (slideId: string, updates: Partial<SlideOutline>) => void;
  reorderOutlineSlides: (fromIndex: number, toIndex: number) => void;
  addOutlineSlide: (afterSlideId?: string) => void;
  removeOutlineSlide: (slideId: string) => void;
  resetOutlineSlideGeneration: (slideId: string) => void;
  revertSlideGenerationStatus: (slideId: string, previousStatus: 'pending' | 'completed') => void;
  setActiveChatContext: (context: 'outline' | 'narration' | string) => void;
  setSlideConversations: (conversations: SlideConversation[]) => void;
  updateSlideConversation: (outlineSlideId: string, updates: Partial<SlideConversation>) => void;
  addSlideConversation: (conversation: SlideConversation) => void;

  // Player Actions
  setPlayerState: (state: Partial<PlayerState>) => void;

  // Edit Mode Actions
  setEditMode: (enabled: boolean) => void;
  toggleEditMode: () => void;
  setEditToolbarState: (state: Partial<EditToolbarState>) => void;

  // Edit Isolation Actions - track which slide is being edited
  setEditingSlide: (conversationId: string | null, slideId: string | null) => void;
  setPendingSlideUpdate: (update: PendingSlideUpdate | null) => void;
  clearPendingSlideUpdate: () => void;
  isEditingSlide: (conversationId: string, slideId: string) => boolean;

  // Inspector Actions
  setActiveInspectorTab: (tab: InspectorTab) => void;

  // Global Actions
  triggerPreviewRefresh: () => void;
  triggerPreviewRefreshWithSeek: (seekTime: number) => void;
  setPendingSeekTime: (time: number | null) => void;
  clearPendingSeekTime: () => void;
  setPreviewSeekToEnd: (value: boolean) => void;
  setConversationId: (id: string | null) => void;
  setOutlineLoading: (loading: boolean) => void;
  setTemplateLayouts: (layouts: TemplateLayout[]) => void;
  setOutlineGenerating: (generating: boolean) => void;
  setOutlineGeneratingThinking: (thinking: string | undefined) => void;
  setOutlineNeedsInput: (needs: boolean) => void;
  setOutlineLocalEditTimestamp: (timestamp: number | null) => void;

  // Cache Actions
  getCachedSlide: (conversationId: string, slideId: string) => CachedSlide | null;
  setCachedSlide: (conversationId: string, slideId: string, data: UnifiedSlide, version: number) => void;
  invalidateSlideCache: (conversationId: string, slideId?: string) => void;

  // Reset
  reset: () => void;
}

type VideoWorkspaceStore = VideoWorkspaceState & VideoWorkspaceActions;

// How long a pick holds the studio against the canvas's reports of other scenes if the canvas never reaches it.
const CANVAS_TARGET_MS = 3000;

const initialState: VideoWorkspaceState = {
  // Panel States - persisted
  leftPanelCollapsed: false,
  rightPanelCollapsed: false,
  leftPanelWidth: 350,
  leftPanelView: 'deck',  // Default to deck view when in vertical mode

  // Filmstrip - persisted
  filmstripMode: 'horizontal',
  filmstripCollapsed: false,

  // === KEYED DATA STRUCTURES ===
  // These provide conversation isolation - accessing data requires conversationId
  slidesByConversation: {},
  currentSlideIdByConversation: {},
  currentSlideDataByConversation: {},
  outlineByConversation: {},
  selectedOutlineSlideIdByConversation: {},
  activeChatContextByConversation: {},
  slideConversationsByParent: {},
  broadcastLogByConversation: {},
  versionsBySlide: {},
  currentVersionBySlide: {},
  servePinBySlide: {},
  backgroundMusicByConversation: {},
  narrationCacheByConversation: {},
  narrationCacheLoading: false,
  voicesCache: null,
  narrationJobStateByConversation: {},
  applyJobStateByConversation: {},
  outlineChangeQueueByConversation: {},

  // === GLOBAL STATE ===
  isSlideLoading: false,

  // Player State
  playerState: {
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    playbackRate: 1,
    isLoading: true,
    slideTitle: '',
  },

  // Edit Mode
  isEditMode: false,
  editToolbarState: {
    selectedCount: 0,
    canUndo: false,
    canRedo: false,
  },

  // Edit Isolation
  editingSlideId: null,
  editingConversationId: null,
  pendingSlideUpdate: null,

  // Inspector
  activeInspectorTab: 'audio',

  // Preview refresh key
  previewRefreshKey: 0,

  // Pending seek time - for restoring position after version switch
  pendingSeekTime: null,

  // Preview mode - seek to end after slide loads (shows completed state by default)
  previewSeekToEnd: true,

  // Context
  conversationId: null,

  // Slide cache (already keyed!)
  slideCache: new Map<string, CachedSlide>(),

  // Global outline state
  outlineLoading: false,
  templateLayouts: [],

  // Outline generation state
  isOutlineGenerating: false,
  outlineGeneratingThinking: undefined,
  outlineNeedsInput: false,

  // Outline local edit tracking
  outlineLocalEditTimestamp: null,

  // === DEPRECATED - Legacy state for backwards compatibility ===
  slides: [],
  currentSlideId: null,
  lastManualSlideSelectAt: 0,
  canvasTarget: null,
  slideThumbBust: {},
  currentSlideData: null,
  outline: null,
  selectedOutlineSlideId: null,
  versions: [],
  currentVersion: null,
  activeChatContext: 'outline',
  slideConversations: [],
};

export const useVideoWorkspaceStore = create<VideoWorkspaceStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // Panel Actions
        setLeftPanelCollapsed: (collapsed) => set({ leftPanelCollapsed: collapsed }),
        setRightPanelCollapsed: (collapsed) => set({ rightPanelCollapsed: collapsed }),
        toggleLeftPanel: () => set((state) => ({ leftPanelCollapsed: !state.leftPanelCollapsed })),
        toggleRightPanel: () => set((state) => ({ rightPanelCollapsed: !state.rightPanelCollapsed })),
        setLeftPanelWidth: (width) => set({ leftPanelWidth: Math.min(Math.max(width, 280), 800) }),
        setLeftPanelView: (view) => set({ leftPanelView: view }),
        toggleLeftPanelView: () => set((state) => ({
          leftPanelView: state.leftPanelView === 'chat' ? 'deck' : 'chat'
        })),

        // Filmstrip Actions
        setFilmstripMode: (mode) => {
          set({ filmstripMode: mode });
          // When switching to vertical mode, show deck by default
          if (mode === 'vertical') {
            set({ leftPanelView: 'deck' });
          }
        },
        toggleFilmstripMode: () => {
          const current = get().filmstripMode;
          const newMode = current === 'horizontal' ? 'vertical' : 'horizontal';
          get().setFilmstripMode(newMode);
        },
        setFilmstripCollapsed: (collapsed) => set({ filmstripCollapsed: collapsed }),
        toggleFilmstrip: () => set((state) => ({ filmstripCollapsed: !state.filmstripCollapsed })),

        // ============================================================
        // SCOPED SLIDE ACTIONS - Use these for conversation isolation
        // ============================================================
        getSlidesForConversation: (conversationId) => {
          return get().slidesByConversation[conversationId] || [];
        },

        getCurrentSlideIdForConversation: (conversationId) => {
          return get().currentSlideIdByConversation[conversationId] ?? null;
        },

        getCurrentSlideDataForConversation: (conversationId) => {
          return get().currentSlideDataByConversation[conversationId] ?? null;
        },

        setSlidesForConversation: (conversationId, slides) => {
          set((state) => ({
            slidesByConversation: {
              ...state.slidesByConversation,
              [conversationId]: slides,
            },
            // Also update legacy state if this is the active conversation
            ...(state.conversationId === conversationId ? { slides } : {}),
          }));
        },

        setCurrentSlideIdForConversation: (conversationId, id) => {
          set((state) => {
            const key = `${conversationId}:${id}`;
            return {
              currentSlideIdByConversation: {
                ...state.currentSlideIdByConversation,
                [conversationId]: id,
              },
              // Also update legacy state if this is the active conversation. The version switcher shows the new scene's
              // stored list, or nothing until it loads, never the list of the scene just left.
              ...(state.conversationId === conversationId
                ? { currentSlideId: id, versions: state.versionsBySlide[key] ?? [], currentVersion: state.currentVersionBySlide[key] ?? null }
                : {}),
            };
          });
        },

        setCurrentSlideDataForConversation: (conversationId, data) => {
          set((state) => ({
            currentSlideDataByConversation: {
              ...state.currentSlideDataByConversation,
              [conversationId]: data,
            },
            // Also update legacy state if this is the active conversation
            ...(state.conversationId === conversationId ? { currentSlideData: data } : {}),
          }));
        },

        applyLoadedSlide: (conversationId, slideId, data) => {
          const state = get();
          if (state.conversationId !== conversationId || state.currentSlideIdByConversation[conversationId] !== slideId) return false;
          state.setCurrentSlideDataForConversation(conversationId, data);
          return true;
        },

        applyLoadedVersions: (conversationId, slideId, versions, currentVersion) => {
          const state = get();
          state.setVersionsForSlide(conversationId, slideId, versions);
          state.setCurrentVersionForSlide(conversationId, slideId, currentVersion);
          return state.conversationId === conversationId && state.currentSlideIdByConversation[conversationId] === slideId;
        },

        navigateToSlideForConversation: (conversationId, direction) => {
          const slides = get().slidesByConversation[conversationId] || [];
          const currentSlideId = get().currentSlideIdByConversation[conversationId];
          if (!slides.length || !currentSlideId) return;

          const currentIndex = slides.findIndex((s) => s.id === currentSlideId);
          if (currentIndex === -1) return;

          const newIndex = direction === 'next'
            ? Math.min(currentIndex + 1, slides.length - 1)
            : Math.max(currentIndex - 1, 0);

          if (newIndex !== currentIndex) {
            get().setCurrentSlideIdForConversation(conversationId, slides[newIndex].id);
          }
        },

        // ============================================================
        // SCOPED OUTLINE ACTIONS - Use these for conversation isolation
        // ============================================================
        getOutlineForConversation: (conversationId) => {
          return get().outlineByConversation[conversationId] ?? null;
        },

        getSelectedOutlineSlideIdForConversation: (conversationId) => {
          return get().selectedOutlineSlideIdByConversation[conversationId] ?? null;
        },

        setOutlineForConversation: (conversationId, outline) => {
          set((state) => ({
            outlineByConversation: {
              ...state.outlineByConversation,
              [conversationId]: outline,
            },
            // Also update legacy state if this is the active conversation
            ...(state.conversationId === conversationId ? { outline } : {}),
          }));
        },

        setSelectedOutlineSlideForConversation: (conversationId, id) => {
          set((state) => ({
            selectedOutlineSlideIdByConversation: {
              ...state.selectedOutlineSlideIdByConversation,
              [conversationId]: id,
            },
            // Also update legacy state if this is the active conversation
            ...(state.conversationId === conversationId ? { selectedOutlineSlideId: id } : {}),
          }));
        },

        updateOutlineTitleForConversation: (conversationId, title) => {
          const outline = get().outlineByConversation[conversationId];
          if (!outline) return;

          get().setOutlineForConversation(conversationId, {
            ...outline,
            title,
          });
        },

        updateOutlineSlideForConversation: (conversationId, slideId, updates) => {
          const outline = get().outlineByConversation[conversationId];
          if (!outline) return;

          const updatedSlides = outline.slides.map((slide) =>
            slide.id === slideId
              ? { ...slide, ...updates, userModified: true }
              : slide
          );

          get().setOutlineForConversation(conversationId, {
            ...outline,
            slides: updatedSlides,
          });
        },

        reorderOutlineSlidesForConversation: (conversationId, fromIndex, toIndex) => {
          const outline = get().outlineByConversation[conversationId];
          if (!outline) return;

          const slides = [...outline.slides];
          const [removed] = slides.splice(fromIndex, 1);
          slides.splice(toIndex, 0, removed);

          const reorderedSlides = slides.map((slide, index) => ({
            ...slide,
            order: index + 1,
          }));

          get().setOutlineForConversation(conversationId, {
            ...outline,
            slides: reorderedSlides,
          });
        },

        addOutlineSlideForConversation: (conversationId, afterSlideId) => {
          const outline = get().outlineByConversation[conversationId];
          if (!outline) return;

          let insertIndex = outline.slides.length;
          if (afterSlideId) {
            const afterIndex = outline.slides.findIndex((s) => s.id === afterSlideId);
            if (afterIndex !== -1) {
              insertIndex = afterIndex + 1;
            }
          }

          const maxSlideNum = outline.slides.reduce((max, slide) => {
            const match = slide.id.match(/slide-(\d+)/);
            if (match) {
              return Math.max(max, parseInt(match[1], 10));
            }
            return max;
          }, 0);
          const newSlideId = `slide-${String(maxSlideNum + 1).padStart(3, '0')}`;

          const newSlide: SlideOutline = {
            id: newSlideId,
            order: insertIndex + 1,
            title: 'New Slide',
            description: 'Describe what this slide should contain...',
            keyPoints: ['Add key point here'],
            layoutId: outline.slides[0]?.layoutId || 'layout_hero',
            layoutName: outline.slides[0]?.layoutName || 'Hero',
            userModified: true,
          };

          const slides = [...outline.slides];
          slides.splice(insertIndex, 0, newSlide);

          const reorderedSlides = slides.map((slide, index) => ({
            ...slide,
            order: index + 1,
          }));

          get().setOutlineForConversation(conversationId, {
            ...outline,
            slides: reorderedSlides,
          });
          get().setSelectedOutlineSlideForConversation(conversationId, newSlideId);
        },

        removeOutlineSlideForConversation: (conversationId, slideId) => {
          const outline = get().outlineByConversation[conversationId];
          const selectedOutlineSlideId = get().selectedOutlineSlideIdByConversation[conversationId];
          if (!outline || outline.slides.length <= 1) return;

          const removedIndex = outline.slides.findIndex((s) => s.id === slideId);
          const filteredSlides = outline.slides.filter((s) => s.id !== slideId);

          const reorderedSlides = filteredSlides.map((slide, index) => ({
            ...slide,
            order: index + 1,
          }));

          let newSelectedId = selectedOutlineSlideId;
          if (selectedOutlineSlideId === slideId) {
            newSelectedId = reorderedSlides[Math.min(removedIndex, reorderedSlides.length - 1)]?.id || null;
          }

          get().setOutlineForConversation(conversationId, {
            ...outline,
            slides: reorderedSlides,
          });
          get().setSelectedOutlineSlideForConversation(conversationId, newSelectedId);
        },

        // ============================================================
        // SCOPED VERSION ACTIONS - Use these for conversation isolation
        // ============================================================
        getVersionsForSlide: (conversationId, slideId) => {
          const key = `${conversationId}:${slideId}`;
          return get().versionsBySlide[key] || [];
        },

        getCurrentVersionForSlide: (conversationId, slideId) => {
          const key = `${conversationId}:${slideId}`;
          return get().currentVersionBySlide[key] ?? null;
        },

        getServePinForSlide: (conversationId, slideId) => {
          const key = `${conversationId}:${slideId}`;
          return get().servePinBySlide[key] ?? null;
        },

        setServePinForSlide: (conversationId, slideId, pin) => {
          const key = `${conversationId}:${slideId}`;
          set((state) => ({
            servePinBySlide: { ...state.servePinBySlide, [key]: pin },
          }));
        },

        setVersionsForSlide: (conversationId, slideId, versions) => {
          const key = `${conversationId}:${slideId}`;
          const state = get();
          const isActiveSlide = state.conversationId === conversationId &&
            state.currentSlideId === slideId;

          set((state) => ({
            versionsBySlide: {
              ...state.versionsBySlide,
              [key]: versions,
            },
            // Also update legacy state if this is the active slide
            ...(isActiveSlide ? { versions } : {}),
          }));
        },

        setCurrentVersionForSlide: (conversationId, slideId, version) => {
          const key = `${conversationId}:${slideId}`;
          const state = get();
          const isActiveSlide = state.conversationId === conversationId &&
            state.currentSlideId === slideId;

          set((state) => ({
            currentVersionBySlide: {
              ...state.currentVersionBySlide,
              [key]: version,
            },
            // Also update legacy state if this is the active slide
            ...(isActiveSlide ? { currentVersion: version } : {}),
          }));
        },

        // ============================================================
        // SCOPED CHAT CONTEXT ACTIONS - Use these for conversation isolation
        // ============================================================
        getActiveChatContextForConversation: (conversationId) => {
          return get().activeChatContextByConversation[conversationId] ?? 'outline';
        },

        getSlideConversationsForParent: (conversationId) => {
          return get().slideConversationsByParent[conversationId] || [];
        },

        setActiveChatContextForConversation: (conversationId, context) => {
          set((state) => ({
            activeChatContextByConversation: {
              ...state.activeChatContextByConversation,
              [conversationId]: context,
            },
            // Also update legacy state if this is the active conversation
            ...(state.conversationId === conversationId ? { activeChatContext: context } : {}),
          }));
        },

        setSlideConversationsForParent: (conversationId, conversations) => {
          set((state) => ({
            slideConversationsByParent: {
              ...state.slideConversationsByParent,
              [conversationId]: conversations,
            },
            // Also update legacy state if this is the active conversation
            ...(state.conversationId === conversationId ? { slideConversations: conversations } : {}),
          }));
        },

        updateSlideConversationForParent: (conversationId, outlineSlideId, updates) => {
          const conversations = get().slideConversationsByParent[conversationId] || [];
          const updatedConversations = conversations.map((conv) =>
            conv.outlineSlideId === outlineSlideId
              ? { ...conv, ...updates }
              : conv
          );
          get().setSlideConversationsForParent(conversationId, updatedConversations);
        },

        addSlideConversationForParent: (conversationId, conversation) => {
          const conversations = get().slideConversationsByParent[conversationId] || [];
          const exists = conversations.some((c) => c.outlineSlideId === conversation.outlineSlideId);
          if (!exists) {
            get().setSlideConversationsForParent(conversationId, [...conversations, conversation]);
          } else {
            get().updateSlideConversationForParent(conversationId, conversation.outlineSlideId, conversation);
          }
        },

        // ============================================================
        // SCOPED BROADCAST LOG ACTIONS - Broadcast to all slides
        // ============================================================
        getBroadcastLogForConversation: (conversationId) => {
          return get().broadcastLogByConversation[conversationId] || [];
        },

        addBroadcastMessage: (conversationId, message) => {
          set((state) => ({
            broadcastLogByConversation: {
              ...state.broadcastLogByConversation,
              [conversationId]: [
                ...(state.broadcastLogByConversation[conversationId] || []),
                message,
              ],
            },
          }));
        },

        clearBroadcastLog: (conversationId) => {
          set((state) => {
            const { [conversationId]: _, ...rest } = state.broadcastLogByConversation;
            return {
              broadcastLogByConversation: rest,
            };
          });
        },

        // ============================================================
        // SCOPED BACKGROUND MUSIC ACTIONS - Video-level music
        // ============================================================
        getBackgroundMusicForConversation: (conversationId) => {
          return get().backgroundMusicByConversation[conversationId] ?? null;
        },

        setBackgroundMusicForConversation: (conversationId, track) => {
          set((state) => ({
            backgroundMusicByConversation: {
              ...state.backgroundMusicByConversation,
              [conversationId]: {
                track,
                volume: state.backgroundMusicByConversation[conversationId]?.volume ?? 0.3,
                loop: state.backgroundMusicByConversation[conversationId]?.loop ?? true,
                isApplied: false,  // Mark as not applied until synced to manifest
                narratorVolume: state.backgroundMusicByConversation[conversationId]?.narratorVolume ?? 1.0,
              },
            },
          }));
        },

        updateBackgroundMusicVolumeForConversation: (conversationId, volume) => {
          const current = get().backgroundMusicByConversation[conversationId];
          if (!current) return;

          set((state) => ({
            backgroundMusicByConversation: {
              ...state.backgroundMusicByConversation,
              [conversationId]: {
                ...current,
                volume: Math.max(0, Math.min(1, volume)),  // Clamp 0-1
              },
            },
          }));
        },

        updateBackgroundMusicLoopForConversation: (conversationId, loop) => {
          const current = get().backgroundMusicByConversation[conversationId];
          if (!current) return;

          set((state) => ({
            backgroundMusicByConversation: {
              ...state.backgroundMusicByConversation,
              [conversationId]: {
                ...current,
                loop,
              },
            },
          }));
        },

        setBackgroundMusicAppliedForConversation: (conversationId, isApplied) => {
          const current = get().backgroundMusicByConversation[conversationId];
          if (!current) return;

          set((state) => ({
            backgroundMusicByConversation: {
              ...state.backgroundMusicByConversation,
              [conversationId]: {
                ...current,
                isApplied,
              },
            },
          }));
        },

        removeBackgroundMusicForConversation: (conversationId) => {
          set((state) => {
            const { [conversationId]: _, ...rest } = state.backgroundMusicByConversation;
            return {
              backgroundMusicByConversation: rest,
            };
          });
        },

        updateNarratorVolumeForConversation: (conversationId, volume) => {
          const current = get().backgroundMusicByConversation[conversationId];
          // Create state if it doesn't exist (narrator volume can be set without background music)
          set((state) => ({
            backgroundMusicByConversation: {
              ...state.backgroundMusicByConversation,
              [conversationId]: {
                track: current?.track ?? null,
                volume: current?.volume ?? 0.3,
                loop: current?.loop ?? true,
                isApplied: current?.isApplied ?? false,
                narratorVolume: Math.max(0, Math.min(1, volume)),  // Clamp 0-1
              },
            },
          }));
        },

        initializeAudioStateFromManifest: (conversationId, config) => {
          const { backgroundMusic, audioMix } = config;
          const current = get().backgroundMusicByConversation[conversationId];

          // Only initialize if we don't already have state (avoid overwriting user changes)
          if (current?.track) return;

          set((state) => ({
            backgroundMusicByConversation: {
              ...state.backgroundMusicByConversation,
              [conversationId]: {
                track: backgroundMusic ? {
                  id: 'manifest',
                  title: backgroundMusic.url.split('/').pop() || 'Background Music',
                  url: backgroundMusic.url,
                  duration: 0,
                  source: 'url' as const,
                } : null,
                volume: backgroundMusic?.volume ?? 0.3,
                loop: backgroundMusic?.loop ?? true,
                isApplied: true,  // Already in manifest
                narratorVolume: audioMix?.narratorVolume ?? 1.0,
              },
            },
          }));
        },

        // ============================================================
        // PROJECT RESOLUTION - Derived from narration context
        // ============================================================
        getProjectResolution: (conversationId) => {
          const parseRes = (s?: string | null) => {
            if (!s) return null;
            const m = s.match(/^(\d+)x(\d+)$/);
            return m ? { width: parseInt(m[1], 10), height: parseInt(m[2], 10) } : null;
          };
          // 1. Check scoped outline
          const scopedOutline = get().outlineByConversation[conversationId];
          const fromScoped = parseRes(scopedOutline?.resolution);
          if (fromScoped) return fromScoped;
          // 2. Check legacy outline (VideoWorkspaceShell uses setOutline)
          const legacyOutline = get().outline;
          const fromLegacy = parseRes(legacyOutline?.resolution);
          if (fromLegacy) return fromLegacy;
          // 3. Fall back to narration context
          const cache = get().narrationCacheByConversation[conversationId];
          const fromNarration = parseRes(cache?.narrativeContext?.resolution);
          if (fromNarration) return fromNarration;
          // 4. Default
          return { width: 1920, height: 1080 };
        },

        // ============================================================
        // SCOPED NARRATION CACHE ACTIONS - For instant tab switching
        // ============================================================
        getNarrationCache: (conversationId) => {
          return get().narrationCacheByConversation[conversationId] ?? null;
        },

        setNarrationCache: (conversationId, cache) => {
          set((state) => ({
            narrationCacheByConversation: {
              ...state.narrationCacheByConversation,
              [conversationId]: cache,
            },
          }));
        },

        updateScriptsInCache: (conversationId, scripts) => {
          const current = get().narrationCacheByConversation[conversationId];
          if (!current) return;

          set((state) => ({
            narrationCacheByConversation: {
              ...state.narrationCacheByConversation,
              [conversationId]: {
                ...current,
                scripts,
                lastFetchedAt: Date.now(),
              },
            },
          }));
        },

        updateAudioStatusInCache: (conversationId, status) => {
          const current = get().narrationCacheByConversation[conversationId];
          if (!current) return;

          set((state) => ({
            narrationCacheByConversation: {
              ...state.narrationCacheByConversation,
              [conversationId]: {
                ...current,
                audioStatus: status,
                lastFetchedAt: Date.now(),
              },
            },
          }));
        },

        updateNarrativeContextInCache: (conversationId, context) => {
          const current = get().narrationCacheByConversation[conversationId];
          if (!current) return;

          set((state) => ({
            narrationCacheByConversation: {
              ...state.narrationCacheByConversation,
              [conversationId]: {
                ...current,
                narrativeContext: context,
                hasNarrativeContext: !!(context?.audioPrompt || context?.videoPromptFlash || context?.videoPromptPro),
                lastFetchedAt: Date.now(),
              },
            },
          }));
        },

        invalidateNarrationCache: (conversationId) => {
          set((state) => {
            const { [conversationId]: _, ...rest } = state.narrationCacheByConversation;
            return {
              narrationCacheByConversation: rest,
            };
          });
        },

        setVoicesCache: (voices, defaultVoice) => {
          set({
            voicesCache: {
              voices,
              defaultVoice,
              lastFetchedAt: Date.now(),
            },
          });
        },

        preloadNarrationData: async (conversationId) => {
          // Prevent concurrent preloads
          if (get().narrationCacheLoading) return;

          set({ narrationCacheLoading: true });

          try {
            // Import services dynamically to avoid circular dependencies
            const { outlineAPI } = await import('@/services/api');
            const { getAudioSettings, getConversationAudioStatus, getAvailableVoices } = await import('@/services/audioService');

            // Fetch all data in parallel
            const [scriptsResponse, settingsResult, audioStatusResult, voicesResult, narrativeContextResult] = await Promise.all([
              outlineAPI.getScripts(conversationId),
              getAudioSettings(conversationId).catch(() => null),
              getConversationAudioStatus(conversationId).catch(() => ({ slides: [] })),
              // Only fetch voices if not cached
              get().voicesCache ? Promise.resolve(null) : getAvailableVoices(),
              // Fetch narrative context for TTS prompts
              outlineAPI.getNarrativeContext(conversationId).catch(() => ({ narrativeContext: null })),
            ]);

            // Update voices cache if fetched
            if (voicesResult) {
              get().setVoicesCache(voicesResult.voices, voicesResult.defaultVoice);
            }

            // Store narration cache
            get().setNarrationCache(conversationId, {
              scripts: scriptsResponse.scripts || [],
              audioSettings: settingsResult ? {
                voiceId: settingsResult.voiceId || 'Kore',
                ttsModel: settingsResult.ttsModel || 'gemini-2.5-flash-tts',
              } : null,
              audioStatus: audioStatusResult.slides || [],
              hasNarrativeContext: scriptsResponse.hasNarrativeContext ?? false,
              narrativeContext: narrativeContextResult.narrativeContext as NarrativeContext | null,
              lastFetchedAt: Date.now(),
            });

            console.log('[VideoWorkspaceStore] Narration data preloaded for:', conversationId);
          } catch (error) {
            console.error('[VideoWorkspaceStore] Failed to preload narration data:', error);
          } finally {
            set({ narrationCacheLoading: false });
          }
        },

        // ============================================================
        // SCOPED NARRATION JOB STATE ACTIONS - Track active script generation
        // ============================================================
        getNarrationJobState: (conversationId) => {
          return get().narrationJobStateByConversation[conversationId] ?? null;
        },

        setNarrationJobState: (conversationId, stateUpdate) => {
          const current = get().narrationJobStateByConversation[conversationId];
          set((state) => ({
            narrationJobStateByConversation: {
              ...state.narrationJobStateByConversation,
              [conversationId]: {
                isGenerating: current?.isGenerating ?? false,
                activeJobId: current?.activeJobId ?? null,
                activeMessageId: current?.activeMessageId ?? null,
                generatingThinking: current?.generatingThinking,
                lastUpdatedAt: Date.now(),
                ...stateUpdate,
              },
            },
          }));
        },

        clearNarrationJobState: (conversationId) => {
          set((state) => {
            const { [conversationId]: _, ...rest } = state.narrationJobStateByConversation;
            return {
              narrationJobStateByConversation: rest,
            };
          });
        },

        // ============================================================
        // SCOPED APPLY JOB STATE ACTIONS - Track audio apply jobs per slide
        // ============================================================
        getApplyJobState: (conversationId) => {
          return get().applyJobStateByConversation[conversationId] ?? null;
        },

        setApplyJobState: (conversationId, stateUpdate) => {
          const current = get().applyJobStateByConversation[conversationId];
          set((state) => ({
            applyJobStateByConversation: {
              ...state.applyJobStateByConversation,
              [conversationId]: {
                slideJobs: current?.slideJobs ?? {},
                isApplyingAll: current?.isApplyingAll ?? false,
                lastUpdatedAt: Date.now(),
                ...stateUpdate,
              },
            },
          }));
        },

        updateSlideApplyJob: (conversationId, slideId, job) => {
          const current = get().applyJobStateByConversation[conversationId];
          set((state) => {
            const slideJobs = { ...(current?.slideJobs ?? {}) };
            if (job === null) {
              delete slideJobs[slideId];
            } else {
              slideJobs[slideId] = job;
            }
            return {
              applyJobStateByConversation: {
                ...state.applyJobStateByConversation,
                [conversationId]: {
                  slideJobs,
                  isApplyingAll: current?.isApplyingAll ?? false,
                  lastUpdatedAt: Date.now(),
                },
              },
            };
          });
        },

        clearApplyJobState: (conversationId) => {
          set((state) => {
            const { [conversationId]: _, ...rest } = state.applyJobStateByConversation;
            return {
              applyJobStateByConversation: rest,
            };
          });
        },

        // ============================================================
        // SCOPED OUTLINE CHANGE QUEUE ACTIONS - Queue outline changes for AI
        // ============================================================
        addOutlineChange: (conversationId, item) => {
          set((state) => {
            const existing = state.outlineChangeQueueByConversation[conversationId] || [];
            let updatedQueue: OutlineChangeQueueItem[];

            if (item.changeType === 'layout_change') {
              // Upsert by slideId: keep the ORIGINAL old value, update to latest new value
              const existingIndex = existing.findIndex(
                (e) => e.slideId === item.slideId && e.changeType === 'layout_change'
              );
              if (existingIndex !== -1) {
                const original = existing[existingIndex];
                // If user changed back to the original layout, remove the entry (net-zero)
                if (original.details.oldValue === item.details.newValue) {
                  updatedQueue = existing.filter((_, i) => i !== existingIndex);
                } else {
                  updatedQueue = [...existing];
                  updatedQueue[existingIndex] = {
                    ...item,
                    details: {
                      ...item.details,
                      oldValue: original.details.oldValue,
                      oldDisplayName: original.details.oldDisplayName,
                    },
                    description: `Layout: ${original.details.oldDisplayName} \u2192 ${item.details.newDisplayName}`,
                  };
                }
              } else {
                updatedQueue = [...existing, item];
              }
            } else if (item.changeType === 'add_slide') {
              // Always append - synthetic slideId never collides
              updatedQueue = [...existing, item];
            } else if (item.changeType === 'delete_slide') {
              // Delete supersedes any existing entry for the same slideId
              updatedQueue = [...existing.filter((e) => e.slideId !== item.slideId), item];
            } else {
              updatedQueue = [...existing, item];
            }

            return {
              outlineChangeQueueByConversation: {
                ...state.outlineChangeQueueByConversation,
                [conversationId]: updatedQueue,
              },
            };
          });
        },

        removeOutlineChange: (conversationId, itemId) => {
          set((state) => ({
            outlineChangeQueueByConversation: {
              ...state.outlineChangeQueueByConversation,
              [conversationId]: (state.outlineChangeQueueByConversation[conversationId] || [])
                .filter((item) => item.id !== itemId),
            },
          }));
        },

        clearOutlineChangeQueue: (conversationId) => {
          set((state) => {
            const { [conversationId]: _, ...rest } = state.outlineChangeQueueByConversation;
            return {
              outlineChangeQueueByConversation: rest,
            };
          });
        },

        getOutlineChangeQueue: (conversationId) => {
          return get().outlineChangeQueueByConversation[conversationId] || [];
        },

        // ============================================================
        // LEGACY SLIDE ACTIONS - Deprecated, use scoped actions above
        // These sync to both keyed and legacy state during migration
        // ============================================================
        setSlides: (slides) => {
          const conversationId = get().conversationId;
          if (conversationId) {
            get().setSlidesForConversation(conversationId, slides);
          } else {
            set({ slides });
          }
        },
        setCurrentSlideId: (id) => {
          const conversationId = get().conversationId;
          if (conversationId) {
            get().setCurrentSlideIdForConversation(conversationId, id);
          } else {
            set({ currentSlideId: id });
          }
        },
        setCurrentSlideData: (data) => {
          const conversationId = get().conversationId;
          if (conversationId) {
            get().setCurrentSlideDataForConversation(conversationId, data);
          } else {
            set({ currentSlideData: data });
          }
        },
        markManualSlideSelect: () => set({ lastManualSlideSelectAt: Date.now() }),
        setCanvasTarget: (slideId) => set({ canvasTarget: { slideId, at: Date.now() } }),
        followCanvasScene: (slideId) => {
          const target = get().canvasTarget;
          if (target && Date.now() - target.at <= CANVAS_TARGET_MS) {
            if (slideId !== target.slideId) return;
            set({ canvasTarget: null });
          }
          if (slideId !== get().currentSlideId) get().setCurrentSlideId(slideId);
        },
        bumpSlideThumbBust: (slideIds) => {
          if (!slideIds.length) return;
          const now = Date.now();
          set((state) => {
            const next = { ...state.slideThumbBust };
            for (const id of slideIds) next[id] = now;
            return { slideThumbBust: next };
          });
        },
        setSlideLoading: (loading) => set({ isSlideLoading: loading }),
        navigateToSlide: (direction) => {
          const { slides, currentSlideId } = get();
          if (!slides.length || !currentSlideId) return;

          const currentIndex = slides.findIndex((s) => s.id === currentSlideId);
          if (currentIndex === -1) return;

          const newIndex = direction === 'next'
            ? Math.min(currentIndex + 1, slides.length - 1)
            : Math.max(currentIndex - 1, 0);

          if (newIndex !== currentIndex) {
            set({ currentSlideId: slides[newIndex].id });
          }
        },

        // Player Actions
        setPlayerState: (state) => set((prev) => ({
          playerState: { ...prev.playerState, ...state }
        })),

        // Edit Mode Actions
        setEditMode: (enabled) => set({ isEditMode: enabled }),
        toggleEditMode: () => set((state) => ({ isEditMode: !state.isEditMode })),
        setEditToolbarState: (state) => set((prev) => ({
          editToolbarState: { ...prev.editToolbarState, ...state }
        })),

        // Edit Isolation Actions - track which slide is being edited
        setEditingSlide: (conversationId, slideId) => set({
          editingConversationId: conversationId,
          editingSlideId: slideId,
        }),
        setPendingSlideUpdate: (update) => set({ pendingSlideUpdate: update }),
        clearPendingSlideUpdate: () => set({ pendingSlideUpdate: null }),
        isEditingSlide: (conversationId, slideId) => {
          const state = get();
          return state.isEditMode &&
            state.editingConversationId === conversationId &&
            state.editingSlideId === slideId;
        },

        // Inspector Actions
        setActiveInspectorTab: (tab) => set({ activeInspectorTab: tab }),

        // Version Actions (legacy - sync with keyed state)
        setVersions: (versions) => {
          const conversationId = get().conversationId;
          const slideId = get().currentSlideId;
          if (conversationId && slideId) {
            get().setVersionsForSlide(conversationId, slideId, versions);
          } else {
            set({ versions });
          }
        },
        setCurrentVersion: (version) => {
          const conversationId = get().conversationId;
          const slideId = get().currentSlideId;
          if (conversationId && slideId) {
            get().setCurrentVersionForSlide(conversationId, slideId, version);
          } else {
            set({ currentVersion: version });
          }
        },
        triggerPreviewRefresh: () => set((state) => ({ previewRefreshKey: state.previewRefreshKey + 1 })),

        // Trigger preview refresh and set pending seek time for restoring playback position
        triggerPreviewRefreshWithSeek: (seekTime) => set((state) => ({
          previewRefreshKey: state.previewRefreshKey + 1,
          pendingSeekTime: seekTime,
        })),

        // Set/clear pending seek time
        setPendingSeekTime: (time) => set({ pendingSeekTime: time }),
        clearPendingSeekTime: () => set({ pendingSeekTime: null }),

        // Preview mode - seek to end on filmstrip click
        setPreviewSeekToEnd: (value) => set({ previewSeekToEnd: value }),

        // Context Actions
        setConversationId: (id) => set({ conversationId: id }),

        // Cache Actions
        getCachedSlide: (conversationId, slideId) => {
          const cacheKey = `${conversationId}:${slideId}`;
          const cached = get().slideCache.get(cacheKey);
          return cached || null;
        },

        setCachedSlide: (conversationId, slideId, data, version) => {
          const cacheKey = `${conversationId}:${slideId}`;
          const newCache = new Map(get().slideCache);
          newCache.set(cacheKey, {
            data,
            version,
            cachedAt: Date.now(),
          });
          set({ slideCache: newCache });
        },

        invalidateSlideCache: (conversationId, slideId) => {
          const currentCache = get().slideCache;
          if (slideId) {
            // Invalidate specific slide
            const cacheKey = `${conversationId}:${slideId}`;
            if (currentCache.has(cacheKey)) {
              const newCache = new Map(currentCache);
              newCache.delete(cacheKey);
              set({ slideCache: newCache });
            }
          } else {
            // Invalidate all slides for this conversation
            const newCache = new Map<string, CachedSlide>();
            currentCache.forEach((value, key) => {
              if (!key.startsWith(`${conversationId}:`)) {
                newCache.set(key, value);
              }
            });
            set({ slideCache: newCache });
          }
        },

        // Outline Actions (legacy - sync with keyed state)
        setOutline: (outline) => {
          const conversationId = get().conversationId;
          if (conversationId) {
            get().setOutlineForConversation(conversationId, outline);
          } else {
            set({ outline });
          }
        },
        setOutlineLoading: (loading) => set({ outlineLoading: loading }),
        setSelectedOutlineSlide: (id) => {
          const conversationId = get().conversationId;
          if (conversationId) {
            get().setSelectedOutlineSlideForConversation(conversationId, id);
          } else {
            set({ selectedOutlineSlideId: id });
          }
        },
        setTemplateLayouts: (layouts) => set({ templateLayouts: layouts }),

        updateOutlineTitle: (title) => {
          const conversationId = get().conversationId;
          if (conversationId) {
            get().updateOutlineTitleForConversation(conversationId, title);
          } else {
            const { outline } = get();
            if (!outline) return;
            set({ outline: { ...outline, title } });
          }
        },

        updateOutlineSlide: (slideId, updates) => {
          const conversationId = get().conversationId;
          if (conversationId) {
            get().updateOutlineSlideForConversation(conversationId, slideId, updates);
          } else {
            const { outline } = get();
            if (!outline) return;
            const updatedSlides = outline.slides.map((slide) =>
              slide.id === slideId
                ? { ...slide, ...updates, userModified: true }
                : slide
            );
            set({ outline: { ...outline, slides: updatedSlides } });
          }
        },

        reorderOutlineSlides: (fromIndex, toIndex) => {
          const conversationId = get().conversationId;
          if (conversationId) {
            get().reorderOutlineSlidesForConversation(conversationId, fromIndex, toIndex);
          } else {
            const { outline } = get();
            if (!outline) return;
            const slides = [...outline.slides];
            const [removed] = slides.splice(fromIndex, 1);
            slides.splice(toIndex, 0, removed);
            const reorderedSlides = slides.map((slide, index) => ({
              ...slide,
              order: index + 1,
            }));
            set({ outline: { ...outline, slides: reorderedSlides } });
          }
        },

        addOutlineSlide: (afterSlideId) => {
          const conversationId = get().conversationId;
          if (conversationId) {
            get().addOutlineSlideForConversation(conversationId, afterSlideId);
          } else {
            const { outline } = get();
            if (!outline) return;

            let insertIndex = outline.slides.length;
            if (afterSlideId) {
              const afterIndex = outline.slides.findIndex((s) => s.id === afterSlideId);
              if (afterIndex !== -1) {
                insertIndex = afterIndex + 1;
              }
            }

            const maxSlideNum = outline.slides.reduce((max, slide) => {
              const match = slide.id.match(/slide-(\d+)/);
              if (match) {
                return Math.max(max, parseInt(match[1], 10));
              }
              return max;
            }, 0);
            const newSlideId = `slide-${String(maxSlideNum + 1).padStart(3, '0')}`;

            const newSlide: SlideOutline = {
              id: newSlideId,
              order: insertIndex + 1,
              title: 'New Slide',
              description: 'Describe what this slide should contain...',
              keyPoints: ['Add key point here'],
              layoutId: outline.slides[0]?.layoutId || 'layout_hero',
              layoutName: outline.slides[0]?.layoutName || 'Hero',
              userModified: true,
            };

            const slides = [...outline.slides];
            slides.splice(insertIndex, 0, newSlide);
            const reorderedSlides = slides.map((slide, index) => ({
              ...slide,
              order: index + 1,
            }));

            set({
              outline: { ...outline, slides: reorderedSlides },
              selectedOutlineSlideId: newSlideId,
            });
          }
        },

        removeOutlineSlide: (slideId) => {
          const conversationId = get().conversationId;
          if (conversationId) {
            get().removeOutlineSlideForConversation(conversationId, slideId);
            return;
          }

          const { outline, selectedOutlineSlideId } = get();
          if (!outline || outline.slides.length <= 1) return;

          const removedIndex = outline.slides.findIndex((s) => s.id === slideId);
          const filteredSlides = outline.slides.filter((s) => s.id !== slideId);

          const reorderedSlides = filteredSlides.map((slide, index) => ({
            ...slide,
            order: index + 1,
          }));

          let newSelectedId = selectedOutlineSlideId;
          if (selectedOutlineSlideId === slideId) {
            // Select the next slide, or previous if last was removed
            newSelectedId = reorderedSlides[Math.min(removedIndex, reorderedSlides.length - 1)]?.id || null;
          }

          set({
            outline: {
              ...outline,
              slides: reorderedSlides,
            },
            selectedOutlineSlideId: newSelectedId,
          });
        },

        // Reset a slide's generation state (used when regenerating)
        resetOutlineSlideGeneration: (slideId) => {
          const conversationId = get().conversationId;
          if (!conversationId) return;

          const outline = get().outlineByConversation[conversationId];
          if (!outline) return;

          const updatedSlides = outline.slides.map((slide) =>
            slide.id === slideId
              ? {
                  ...slide,
                  slideConversationId: undefined,
                  generationStatus: 'pending' as const,
                  generationError: undefined,
                  generatedThumbnail: undefined,
                }
              : slide
          );

          get().setOutlineForConversation(conversationId, {
            ...outline,
            slides: updatedSlides,
          });
        },

        // Revert slide generation status (non-destructive - keeps slideConversationId intact)
        // This is the NON-DESTRUCTIVE version of resetOutlineSlideGeneration
        revertSlideGenerationStatus: (slideId, previousStatus) => {
          const conversationId = get().conversationId;
          if (!conversationId) return;

          const outline = get().outlineByConversation[conversationId];
          if (!outline) return;

          const updatedSlides = outline.slides.map((slide) =>
            slide.id === slideId
              ? {
                  ...slide,
                  // Keep slideConversationId intact - this is the key difference!
                  generationStatus: previousStatus as SlideGenerationStatus,
                  generationError: undefined,
                  generatingThinking: undefined,
                  generationStartedAt: undefined,
                }
              : slide
          );

          get().setOutlineForConversation(conversationId, {
            ...outline,
            slides: updatedSlides,
          });

          // Also update the corresponding slideConversation entry
          const slideConversations = get().slideConversationsByParent[conversationId] || [];
          const updatedConversations = slideConversations.map((conv) =>
            conv.outlineSlideId === slideId
              ? {
                  ...conv,
                  status: previousStatus as SlideGenerationStatus,
                  generatingThinking: undefined,
                  errorMessage: undefined,
                }
              : conv
          );
          get().setSlideConversationsForParent(conversationId, updatedConversations);
        },

        // Outline Generation Actions
        setOutlineGenerating: (generating) => set({ isOutlineGenerating: generating }),
        setOutlineGeneratingThinking: (thinking) => set({ outlineGeneratingThinking: thinking }),
        setOutlineNeedsInput: (needs) => set({ outlineNeedsInput: needs }),
        setOutlineLocalEditTimestamp: (timestamp) => set({ outlineLocalEditTimestamp: timestamp }),

        // Chat Context Actions (legacy - sync with keyed state)
        setActiveChatContext: (context) => {
          const conversationId = get().conversationId;
          if (conversationId) {
            get().setActiveChatContextForConversation(conversationId, context);
          } else {
            set({ activeChatContext: context });
          }
        },
        setSlideConversations: (conversations) => {
          const conversationId = get().conversationId;
          if (conversationId) {
            get().setSlideConversationsForParent(conversationId, conversations);
          } else {
            set({ slideConversations: conversations });
          }
        },
        updateSlideConversation: (outlineSlideId, updates) => {
          const conversationId = get().conversationId;
          if (conversationId) {
            get().updateSlideConversationForParent(conversationId, outlineSlideId, updates);
          } else {
            const { slideConversations } = get();
            const updatedConversations = slideConversations.map((conv) =>
              conv.outlineSlideId === outlineSlideId
                ? { ...conv, ...updates }
                : conv
            );
            set({ slideConversations: updatedConversations });
          }
        },
        addSlideConversation: (conversation) => {
          const conversationId = get().conversationId;
          if (conversationId) {
            get().addSlideConversationForParent(conversationId, conversation);
          } else {
            const { slideConversations } = get();
            const exists = slideConversations.some((c) => c.outlineSlideId === conversation.outlineSlideId);
            if (!exists) {
              set({ slideConversations: [...slideConversations, conversation] });
            } else {
              get().updateSlideConversation(conversation.outlineSlideId, conversation);
            }
          }
        },

        // Reset
        reset: () => set({
          ...initialState,
          // Keep persisted panel preferences
          leftPanelCollapsed: get().leftPanelCollapsed,
          rightPanelCollapsed: get().rightPanelCollapsed,
          leftPanelWidth: get().leftPanelWidth,
          leftPanelView: get().leftPanelView,
          filmstripMode: get().filmstripMode,
          filmstripCollapsed: get().filmstripCollapsed,
        }),
      }),
      {
        name: 'video-workspace-storage',
        // Only persist panel and filmstrip preferences
        partialize: (state) => ({
          leftPanelCollapsed: state.leftPanelCollapsed,
          rightPanelCollapsed: state.rightPanelCollapsed,
          leftPanelWidth: state.leftPanelWidth,
          leftPanelView: state.leftPanelView,
          filmstripMode: state.filmstripMode,
          filmstripCollapsed: state.filmstripCollapsed,
          activeInspectorTab: state.activeInspectorTab,
        }),
      }
    ),
    { name: 'VideoWorkspaceStore' }
  )
);

// Selector hooks for common patterns
export const useVideoWorkspacePanels = () => useVideoWorkspaceStore((state) => ({
  leftPanelCollapsed: state.leftPanelCollapsed,
  rightPanelCollapsed: state.rightPanelCollapsed,
  leftPanelWidth: state.leftPanelWidth,
  toggleLeftPanel: state.toggleLeftPanel,
  toggleRightPanel: state.toggleRightPanel,
  setLeftPanelWidth: state.setLeftPanelWidth,
}));

export const useVideoWorkspaceFilmstrip = () => useVideoWorkspaceStore((state) => ({
  filmstripMode: state.filmstripMode,
  filmstripCollapsed: state.filmstripCollapsed,
  setFilmstripMode: state.setFilmstripMode,
  toggleFilmstripMode: state.toggleFilmstripMode,
  toggleFilmstrip: state.toggleFilmstrip,
}));

export const useVideoWorkspaceSlides = () => useVideoWorkspaceStore((state) => ({
  slides: state.slides,
  currentSlideId: state.currentSlideId,
  currentSlideData: state.currentSlideData,
  isSlideLoading: state.isSlideLoading,
  setSlides: state.setSlides,
  setCurrentSlideId: state.setCurrentSlideId,
  setCurrentSlideData: state.setCurrentSlideData,
  navigateToSlide: state.navigateToSlide,
}));

export const useVideoWorkspacePlayer = () => useVideoWorkspaceStore((state) => ({
  playerState: state.playerState,
  setPlayerState: state.setPlayerState,
  isEditMode: state.isEditMode,
  setEditMode: state.setEditMode,
  toggleEditMode: state.toggleEditMode,
  editToolbarState: state.editToolbarState,
  setEditToolbarState: state.setEditToolbarState,
  // Edit isolation
  editingSlideId: state.editingSlideId,
  editingConversationId: state.editingConversationId,
  pendingSlideUpdate: state.pendingSlideUpdate,
  setEditingSlide: state.setEditingSlide,
  setPendingSlideUpdate: state.setPendingSlideUpdate,
  clearPendingSlideUpdate: state.clearPendingSlideUpdate,
  isEditingSlide: state.isEditingSlide,
}));

export const useVideoWorkspaceOutline = () => useVideoWorkspaceStore((state) => ({
  outline: state.outline,
  outlineLoading: state.outlineLoading,
  selectedOutlineSlideId: state.selectedOutlineSlideId,
  templateLayouts: state.templateLayouts,
  isOutlineGenerating: state.isOutlineGenerating,
  outlineGeneratingThinking: state.outlineGeneratingThinking,
  outlineNeedsInput: state.outlineNeedsInput,
  outlineLocalEditTimestamp: state.outlineLocalEditTimestamp,
  setOutline: state.setOutline,
  setOutlineLoading: state.setOutlineLoading,
  setSelectedOutlineSlide: state.setSelectedOutlineSlide,
  setTemplateLayouts: state.setTemplateLayouts,
  setOutlineGenerating: state.setOutlineGenerating,
  setOutlineGeneratingThinking: state.setOutlineGeneratingThinking,
  setOutlineNeedsInput: state.setOutlineNeedsInput,
  setOutlineLocalEditTimestamp: state.setOutlineLocalEditTimestamp,
  updateOutlineTitle: state.updateOutlineTitle,
  updateOutlineSlide: state.updateOutlineSlide,
  reorderOutlineSlides: state.reorderOutlineSlides,
  addOutlineSlide: state.addOutlineSlide,
  removeOutlineSlide: state.removeOutlineSlide,
}));

// ============================================================
// SCOPED SELECTOR HOOKS - Use these for conversation isolation
// These hooks return data scoped to a specific conversationId
// ============================================================

/**
 * Scoped slides hook - returns slide data for a specific conversation
 * Use this instead of useVideoWorkspaceSlides for conversation isolation
 */
export const useScopedSlides = (conversationId: string | null) =>
  useVideoWorkspaceStore((state) => ({
    slides: conversationId ? (state.slidesByConversation[conversationId] || []) : [],
    currentSlideId: conversationId ? (state.currentSlideIdByConversation[conversationId] ?? null) : null,
    currentSlideData: conversationId ? (state.currentSlideDataByConversation[conversationId] ?? null) : null,
    isSlideLoading: state.isSlideLoading,
    // Scoped setters
    setSlides: state.setSlidesForConversation,
    setCurrentSlideId: state.setCurrentSlideIdForConversation,
    setCurrentSlideData: state.setCurrentSlideDataForConversation,
    navigateToSlide: state.navigateToSlideForConversation,
  }));

/**
 * Scoped outline hook - returns outline data for a specific conversation
 * Use this instead of useVideoWorkspaceOutline for conversation isolation
 */
export const useScopedOutline = (conversationId: string | null) =>
  useVideoWorkspaceStore((state) => ({
    outline: conversationId ? (state.outlineByConversation[conversationId] ?? null) : null,
    outlineLoading: state.outlineLoading,
    selectedOutlineSlideId: conversationId
      ? (state.selectedOutlineSlideIdByConversation[conversationId] ?? null)
      : null,
    templateLayouts: state.templateLayouts,
    isOutlineGenerating: state.isOutlineGenerating,
    outlineGeneratingThinking: state.outlineGeneratingThinking,
    // Scoped setters
    setOutline: state.setOutlineForConversation,
    setOutlineLoading: state.setOutlineLoading,
    setSelectedOutlineSlide: state.setSelectedOutlineSlideForConversation,
    setTemplateLayouts: state.setTemplateLayouts,
    setOutlineGenerating: state.setOutlineGenerating,
    setOutlineGeneratingThinking: state.setOutlineGeneratingThinking,
    updateOutlineTitle: state.updateOutlineTitleForConversation,
    updateOutlineSlide: state.updateOutlineSlideForConversation,
    reorderOutlineSlides: state.reorderOutlineSlidesForConversation,
    addOutlineSlide: state.addOutlineSlideForConversation,
    removeOutlineSlide: state.removeOutlineSlideForConversation,
  }));

/**
 * Scoped versions hook - returns version data for a specific slide in a conversation
 * Use this for conversation-isolated version history
 */
export const useScopedVersions = (conversationId: string | null, slideId: string | null) =>
  useVideoWorkspaceStore((state) => {
    const key = conversationId && slideId ? `${conversationId}:${slideId}` : '';
    return {
      versions: key ? (state.versionsBySlide[key] || []) : [],
      currentVersion: key ? (state.currentVersionBySlide[key] ?? null) : null,
      // Scoped setters
      setVersions: state.setVersionsForSlide,
      setCurrentVersion: state.setCurrentVersionForSlide,
    };
  });

/**
 * Scoped chat context hook - returns chat context for a specific conversation
 * Use this for conversation-isolated slide generation context
 */
export const useScopedChatContext = (conversationId: string | null) =>
  useVideoWorkspaceStore((state) => ({
    activeChatContext: conversationId
      ? (state.activeChatContextByConversation[conversationId] ?? 'outline')
      : 'outline',
    slideConversations: conversationId
      ? (state.slideConversationsByParent[conversationId] || [])
      : [],
    broadcastLog: conversationId
      ? (state.broadcastLogByConversation[conversationId] || [])
      : [],
    // Scoped setters
    setActiveChatContext: state.setActiveChatContextForConversation,
    setSlideConversations: state.setSlideConversationsForParent,
    updateSlideConversation: state.updateSlideConversationForParent,
    addSlideConversation: state.addSlideConversationForParent,
    addBroadcastMessage: state.addBroadcastMessage,
    clearBroadcastLog: state.clearBroadcastLog,
  }));

/**
 * Scoped background music hook - returns background music state for a specific conversation
 * Use this for video-level background music control and audio mixing
 */
export const useScopedBackgroundMusic = (conversationId: string | null) =>
  useVideoWorkspaceStore((state) => ({
    backgroundMusic: conversationId
      ? (state.backgroundMusicByConversation[conversationId] ?? null)
      : null,
    // Scoped setters
    setBackgroundMusic: state.setBackgroundMusicForConversation,
    updateVolume: state.updateBackgroundMusicVolumeForConversation,
    updateLoop: state.updateBackgroundMusicLoopForConversation,
    updateNarratorVolume: state.updateNarratorVolumeForConversation,
    setApplied: state.setBackgroundMusicAppliedForConversation,
    removeBackgroundMusic: state.removeBackgroundMusicForConversation,
    initializeFromManifest: state.initializeAudioStateFromManifest,
  }));

/**
 * Scoped narration hook - returns cached narration data for a specific conversation
 * Use this for instant tab switching to narration view
 */
export const useScopedNarration = (conversationId: string | null) =>
  useVideoWorkspaceStore((state) => ({
    cache: conversationId
      ? (state.narrationCacheByConversation[conversationId] ?? null)
      : null,
    isLoading: state.narrationCacheLoading,
    voices: state.voicesCache?.voices ?? [],
    defaultVoice: state.voicesCache?.defaultVoice ?? 'Kore',
    // Job state (persists across navigation)
    jobState: conversationId
      ? (state.narrationJobStateByConversation[conversationId] ?? null)
      : null,
    // Scoped setters
    updateScripts: state.updateScriptsInCache,
    updateAudioStatus: state.updateAudioStatusInCache,
    updateNarrativeContext: state.updateNarrativeContextInCache,
    invalidateCache: state.invalidateNarrationCache,
    preloadData: state.preloadNarrationData,
    // Job state setters
    setJobState: state.setNarrationJobState,
    clearJobState: state.clearNarrationJobState,
  }));

/**
 * Scoped outline change queue hook - returns queued outline changes for a specific conversation
 * Use this to display and manage pending layout changes that need AI processing
 */
export const useScopedOutlineChangeQueue = (conversationId: string | null) =>
  useVideoWorkspaceStore((state) => ({
    queue: conversationId
      ? (state.outlineChangeQueueByConversation[conversationId] || [])
      : [],
    addOutlineChange: state.addOutlineChange,
    removeOutlineChange: state.removeOutlineChange,
    clearOutlineChangeQueue: state.clearOutlineChangeQueue,
  }));
